import { Button, Modal, Checkbox, message } from "antd";
import { Ticket, AlertCircle, Package } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { TInventory } from "~/types/inventory";
import { TServiceTicket } from "~/types";
import { useBulkManageInventory } from "~/services/service-ticket/service-ticket.mutations";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking/keys";
import dayjs from "dayjs";
import { JobStatus, ServiceType } from "~/utils/constants";

interface InventoryManageTicketsModalProps {
  open: boolean;
  onClose: () => void;
  selectedInventories: TInventory[];
  serviceTickets: TServiceTicket[];
  onSuccess?: () => void;
}

export const InventoryManageTicketsModal: React.FC<InventoryManageTicketsModalProps> = ({
  open,
  onClose,
  selectedInventories,
  serviceTickets,
  onSuccess,
}) => {
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { mutate: bulkManageInventory, isPending } = useBulkManageInventory();

  // Compute which tickets already contain ALL selected inventories (for pre-checking)
  const ticketsWithAllSelectedInventories = useMemo(() => {
    if (selectedInventories.length === 0) return new Set<string>();

    const selectedIds = new Set(selectedInventories.map((inv) => inv.id));

    return new Set(
      serviceTickets
        .filter((ticket) => {
          const ticketInventoryIds = new Set(
            (ticket.inventories || []).map((inv) => inv.id)
          );
          // Check if ALL selected inventories are in this ticket
          return [...selectedIds].every((id) => ticketInventoryIds.has(id));
        })
        .map((t) => t.id)
    );
  }, [selectedInventories, serviceTickets]);

  // Pre-check tickets that already have all selected inventories
  useEffect(() => {
    if (open) {
      setSelectedTicketIds(Array.from(ticketsWithAllSelectedInventories));
    }
  }, [open, ticketsWithAllSelectedInventories]);

  const handleToggleTicket = (ticketId: string) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTicketIds.length === serviceTickets.length) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(serviceTickets.map((t) => t.id));
    }
  };

  const handleAction = (action: "add" | "remove") => {
    if (selectedTicketIds.length === 0) {
      message.warning("Please select at least one service ticket.");
      return;
    }

    bulkManageInventory(
      {
        inventoryIds: selectedInventories.map((inv) => inv.id),
        serviceTicketIds: selectedTicketIds,
        action,
      },
      {
        onSuccess: (response: any) => {
          message.success(response.message || `Successfully ${action === "add" ? "added" : "removed"} inventory items.`);
          // Invalidate booking queries to refresh data
          queryClient.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKING] });
          queryClient.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKINGS] });
          onClose();
          onSuccess?.();
        },
        onError: (error: any) => {
          console.error("Bulk manage inventory error:", error);
          const errorMessage =
            error?.response?.data?.message || error?.message || "Unknown error";
          message.error(`Failed to ${action} inventory: ${errorMessage}`, 10);
        },
      }
    );
  };

  const getStatusColor = (status?: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return "bg-green-100 text-green-800";
      case JobStatus.SCHEDULED:
        return "bg-blue-100 text-blue-800";
      case JobStatus.DRAFT:
        return "bg-gray-100 text-gray-800";
      case JobStatus.IN_PROGRESS:
        return "bg-yellow-100 text-yellow-800";
      case JobStatus.CANCELLED:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatServiceType = (type?: ServiceType) => {
    if (!type) return "Unknown";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const itemCount = selectedInventories.length;
  const ticketCount = selectedTicketIds.length;

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <Ticket className="text-purple-600" size={20} />
          <span>Manage Service Tickets</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      {/* Info Banner */}
      <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
        <div className="flex items-start space-x-2">
          <AlertCircle className="text-purple-600 mt-0.5" size={16} />
          <div>
            <p className="text-sm text-purple-800 font-medium">
              Managing {itemCount} inventory item{itemCount !== 1 ? "s" : ""}
            </p>
            <p className="text-xs text-purple-600 mt-1">
              Select service tickets below to add or remove the selected
              inventory items.
            </p>
          </div>
        </div>
      </div>

      {/* Selected Items Preview */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Selected Inventory:
        </h4>
        <div className="max-h-24 overflow-y-auto border rounded-lg p-2 bg-gray-50">
          {selectedInventories.slice(0, 5).map((item) => (
            <div
              key={item.id}
              className="text-xs text-gray-600 py-1 flex items-center space-x-2"
            >
              <Package size={12} className="text-gray-400" />
              <span>
                {item.inventoryRef} - {item.title || "Untitled"}
              </span>
            </div>
          ))}
          {selectedInventories.length > 5 && (
            <div className="text-xs text-gray-500 py-1">
              +{selectedInventories.length - 5} more items...
            </div>
          )}
        </div>
      </div>

      {/* Service Tickets Selection */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">
            Service Tickets in this Booking:
          </h4>
          <Button type="link" size="small" onClick={handleSelectAll}>
            {selectedTicketIds.length === serviceTickets.length
              ? "Deselect All"
              : "Select All"}
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto border rounded-lg bg-white">
          {serviceTickets.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No service tickets found in this booking.
            </div>
          ) : (
            serviceTickets.map((ticket) => {
              const hasAllItems = ticketsWithAllSelectedInventories.has(
                ticket.id
              );
              const isSelected = selectedTicketIds.includes(ticket.id);

              return (
                <div
                  key={ticket.id}
                  className={`p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                    isSelected ? "bg-purple-50" : ""
                  }`}
                  onClick={() => handleToggleTicket(ticket.id)}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleTicket(ticket.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">
                          {ticket.serial || "No Serial"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {ticket.status || "Unknown"}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span>{formatServiceType(ticket.serviceType)}</span>
                        <span>
                          {ticket.startDatetime
                            ? dayjs(ticket.startDatetime).format("MMM D, YYYY")
                            : "No date"}
                        </span>
                        <span>
                          {(ticket.inventories || []).length} items
                        </span>
                      </div>
                      {hasAllItems && (
                        <div className="mt-1">
                          <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            Already has all selected items
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t">
        <span className="text-sm text-gray-500">
          {ticketCount} ticket{ticketCount !== 1 ? "s" : ""} selected
        </span>
        <div className="flex space-x-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            danger
            onClick={() => handleAction("remove")}
            loading={isPending}
            disabled={ticketCount === 0}
          >
            Remove from Selected
          </Button>
          <Button
            type="primary"
            onClick={() => handleAction("add")}
            loading={isPending}
            disabled={ticketCount === 0}
            className="bg-purple-600 hover:bg-purple-700 border-purple-600"
          >
            Add to Selected
          </Button>
        </div>
      </div>
    </Modal>
  );
};
